import { Header } from '@/components/ImportHistory/Header';
import Loader from '@/components/Loader';
import ScrolledCard from '@/components/ScrolledCard';
import Constants from '@/Constants';
import { useGet } from '@/hooks/useGet';
import { usePrevious } from '@/lib/hooks';
import { EnrollmentProcess, EnrollmentProcessItem, ImportStatus } from '@/models/EnrollmentProcess';
import { getAuth } from '@/services/IdentityService';
import { getDisplayName, getHost } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import './style.scss';

interface Props {
    processItem: EnrollmentProcess;
    show: boolean;
    onClose: CallableFunction;
}

export default function ImportHistoryModal({ processItem, show, onClose }: Props) {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [search, setSearch] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const strings = useSelector((state: ApplicationState) => state.locale.data);
    const token = getAuth();
    const prevSearch = usePrevious(search);

    const getKey = () => {
        const offset: number = (page - 1) * limit;
        let queryParams: string = `?offset=${offset}&limit=${limit}`;

        if (search) {
            queryParams = queryParams.concat(`&email=${encodeURIComponent(search)}`);
        }

        let status;
        if (success) status = ImportStatus.COMPLETED;
        if (error) status = ImportStatus.COMPLETED_WITH_ERRORS;
        if (success && error) status = undefined;

        if (status) {
            queryParams = queryParams.concat(`&status=${status}`);
        }

        return `${Constants.LMS_PROGRAM_CLASS_MASS_IMPORT_DETAILS_URI.replace('%p', String(processItem.programClassId)).replace(
            '%e',
            String(processItem.id),
        )}${queryParams}`;
    };

    const { data, loading } = useGet<EnrollmentProcess>(getKey());

    const getErrorsURI = () => {
        return `${getHost()}${Constants.LMS_ERROR_PROGRAM_CLASS_IMPORT_URI.replace('%p', String(processItem.programClassId))
            .replace('%e', String(processItem.id))
            .replace('%t', String(token))}`;
    };

    const getPersonEmail = (importDetail: EnrollmentProcessItem) =>
        importDetail.person ? importDetail.person.email : JSON.parse(importDetail.importData).email;

    const getMessageByStatusCode = (statusCode: string): string => {
        switch (statusCode) {
            case 'SUCCESS':
                return strings.ENROLLMENT_ITEM_ERRORS.SUCCESS;
            case 'ALREADY_ENROLLED_IN_THE_CLASS':
                return strings.ENROLLMENT_ITEM_ERRORS.ALREADY_ENROLLED_IN_THE_CLASS;
            case 'ALREADY_ENROLLED_IN_ANOTHER_CLASS':
                return strings.ENROLLMENT_ITEM_ERRORS.ALREADY_ENROLLED_IN_ANOTHER_CLASS;
            case 'INVALID_EMAIL':
                return strings.ENROLLMENT_ITEM_ERRORS.INVALID_EMAIL;
            case 'PERSON_NOT_FOUND':
                return strings.ENROLLMENT_ITEM_ERRORS.PERSON_NOT_FOUND;
            case 'INACTIVE_USER':
                return strings.ENROLLMENT_ITEM_ERRORS.INACTIVE_USER;
            default:
                return '';
        }
    };

    useEffect(() => {
        setPage(1);
    }, [limit]);

    useEffect(() => {
        if (prevSearch !== search) setPage(1);
    }, [search]);

    return (
        <Modal
            backdrop="static"
            centered
            contentClassName="import-modal"
            dialogClassName="import-modal"
            keyboard={false}
            onHide={onClose}
            show={show}
        >
            <Modal.Body className="alert-modal-content modal-content import-modal import-modal-content">
                <div className="modal-header">
                    <div className="close-icon">
                        <i className="material-icons align-middle" onClick={() => onClose()}>
                            close
                        </i>
                    </div>
                </div>
                <Header
                    error={error}
                    errorsUri={getErrorsURI()}
                    itemsCount={data?.itemsCount}
                    limit={limit}
                    page={page}
                    person={data?.person}
                    search={search}
                    setError={setError}
                    setLimit={setLimit}
                    setPage={setPage}
                    setSearch={setSearch}
                    setSuccess={setSuccess}
                    status={data?.status}
                    success={success}
                />

                <ScrolledCard list={data}>
                    {loading && <Loader />}
                    {!loading && data && data.itemsCount > 0 && (
                        <table className="table table-borderless table-sm table-striped">
                            <thead>
                                <tr>
                                    <th scope="col">{strings.NAME}</th>
                                    <th scope="col">{strings.EMAIL}</th>
                                    <th scope="col" style={{ width: '20%' }}>
                                        {strings.STATUS}
                                    </th>
                                    <th scope="col" style={{ width: '40%' }}>
                                        {strings.DETAILS}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {data &&
                                    data.enrollmentProcessItems.map((importDetail) => (
                                        <tr className="table-row" key={importDetail.id}>
                                            <td scope="row">{!!importDetail.person ? getDisplayName(importDetail.person) : '-'}</td>
                                            <td scope="row">{getPersonEmail(importDetail)}</td>
                                            <td>{strings[importDetail.status as keyof LocaleData]}</td>
                                            <td className={!importDetail.person || importDetail.status === 'COMPLETED_WITH_ERRORS' ? 'error' : ''}>
                                                {data.errorItemsCount ? getMessageByStatusCode(importDetail.statusCode) : strings.CONCLUDED}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    )}
                    {data && data.itemsCount <= 0 && <p className="emptySection">{strings.EMPTY_IMPORT_DETAIL}</p>}
                </ScrolledCard>
            </Modal.Body>
        </Modal>
    );
}
